import { useState, useEffect, useCallback } from "react";
import { Button } from "./ui/button";
import { Input } from "./ui/input";
import { Label } from "./ui/label";
import { Checkbox } from "./ui/checkbox";
import { Badge } from "./ui/badge";
import {
  Dialog,
  DialogContent,
  DialogHeader,
  DialogTitle,
  DialogFooter,
  DialogDescription,
} from "./ui/dialog";
import {
  Trophy,
  Printer,
  Search,
  Loader2,
  Plus,
  X,
  UserSearch,
} from "lucide-react";
import { patientApi } from "../services/api";
import { toast } from "sonner";

const STORAGE_KEY = "tabel_fotbal_preset_v1";

const DEFAULT_PRESET = {
  club_nume: "AFC CÂMPULUNG MUSCEL 2022",
  club_cif: "25852601",
  club_adresa: "ION GIURCULESCU NR 24",
  club_telefon: "0752172359",
  nr_inregistrare: "",
  locul: "",
  director_sportiv: "",
  medic: "NICOLA VIOREL",
};

const CONCLUZIE_LABEL = {
  APT: "APT ANTRENAMENTE ȘI COMPETIȚII",
  APT_SUPRAVEGHERE: "APT ANTRENAMENTE ȘI COMPETIȚII SUB SUPRAVEGHERE MEDICALĂ",
  APT_INDIVIDUALIZAT: "APT ANTRENAMENTE INDIVIDUALIZATE SUB SUPRAVEGHERE",
  APT_CONDITIONAT: "APT CONDIȚIONAT SUB SUPRAVEGHERE MEDICALĂ",
};

const todayISO = () => {
  const d = new Date();
  return `${d.getFullYear()}-${String(d.getMonth() + 1).padStart(2, "0")}-${String(d.getDate()).padStart(2, "0")}`;
};

const fmtRoFromISO = (iso) => {
  if (!iso) return "";
  const [y, m, d] = iso.split("-");
  return `${d}.${m}.${y}`;
};

const addMonthsISO = (iso, months) => {
  if (!iso) return "";
  const d = new Date(iso + "T00:00:00");
  d.setMonth(d.getMonth() + Number(months || 0));
  return `${d.getFullYear()}-${String(d.getMonth() + 1).padStart(2, "0")}-${String(d.getDate()).padStart(2, "0")}`;
};

const upperName = (p) => `${(p?.date_personale?.nume || "").toUpperCase()} ${(p?.date_personale?.prenume || "").toUpperCase()}`.replace(/\s+/g, " ").trim();

export default function TabelFotbalDialog({ open, onOpenChange, initialAthletes, contextLabel }) {
  const [loading, setLoading] = useState(false);
  const [eligible, setEligible] = useState([]);
  const [selected, setSelected] = useState({}); // { [patientId]: patient }
  const [preset, setPreset] = useState(DEFAULT_PRESET);
  const [valStart, setValStart] = useState(todayISO());
  const [valMonths, setValMonths] = useState(6);

  // Search
  const [searchOpen, setSearchOpen] = useState(false);
  const [searchQ, setSearchQ] = useState("");
  const [searchLoading, setSearchLoading] = useState(false);
  const [searchResults, setSearchResults] = useState([]);
  // Filtre suplimentare (client-side) peste rezultatele căutării
  const [filterClub, setFilterClub] = useState("");
  const [filterAntrenor, setFilterAntrenor] = useState("");
  const [filterAn, setFilterAn] = useState("");

  const loadEligible = useCallback(async () => {
    setLoading(true);
    try {
      const list = await patientApi.eligibleFotbal();
      setEligible(list || []);
      // Auto-select all eligible
      const sel = {};
      (list || []).forEach((p) => { sel[p.id] = p; });
      setSelected(sel);
    } catch (e) {
      toast.error("Nu am putut încărca lista de fotbaliști.");
    } finally {
      setLoading(false);
    }
  }, []);

  useEffect(() => {
    if (!open) return;
    // Load persisted preset
    try {
      const raw = localStorage.getItem(STORAGE_KEY);
      if (raw) setPreset({ ...DEFAULT_PRESET, ...JSON.parse(raw) });
    } catch (e) { /* ignore */ }
    setValStart(todayISO());
    setValMonths(6);
    // Dacă avem o listă pre-populată (ex: sportivii unui antrenor), o folosim direct
    // și sărim peste fetch-ul global de fotbaliști eligibili.
    if (Array.isArray(initialAthletes)) {
      setEligible(initialAthletes);
      const sel = {};
      initialAthletes.forEach((p) => { sel[p.id] = p; });
      setSelected(sel);
      setLoading(false);
    } else {
      loadEligible();
    }
  }, [open, loadEligible, initialAthletes]);

  // Live search — declanșat de searchQ (min 2 char) SAU de orice filtru non-gol
  useEffect(() => {
    if (!searchOpen) return;
    const q = searchQ.trim();
    const hasFilter = filterClub.trim() || filterAntrenor.trim() || filterAn.trim();
    if (q.length < 2 && !hasFilter) { setSearchResults([]); return; }
    setSearchLoading(true);
    const t = setTimeout(async () => {
      try {
        // Când există filtre dar nu text, folosim un query-marker cu spațiu pentru
        // a returna toate rezultatele (backend acceptă chiar și query mic + filtrăm client-side).
        const effectiveQ = q.length >= 2 ? q : (filterClub.trim() || filterAntrenor.trim() || filterAn.trim() || "");
        const list = await patientApi.searchPatients(effectiveQ);
        setSearchResults(list || []);
      } catch (e) {
        toast.error("Eroare la căutare.");
      } finally {
        setSearchLoading(false);
      }
    }, 300);
    return () => clearTimeout(t);
  }, [searchQ, searchOpen, filterClub, filterAntrenor, filterAn]);

  // Rezultate filtrate suplimentar client-side pe Club/Antrenor/An naștere
  const filteredResults = (() => {
    const nrm = (s) => String(s || "").toLowerCase().trim();
    const fc = nrm(filterClub);
    const fa = nrm(filterAntrenor);
    const fy = filterAn.trim();
    return searchResults.filter((p) => {
      const dp = p.date_personale || {};
      const is = p.info_sportive || {};
      if (fc && !nrm(is.club_sportiv).includes(fc)) return false;
      if (fa && !nrm(is.antrenor).includes(fa)) return false;
      if (fy) {
        // Data naștere ISO YYYY-MM-DD → an
        const y = (dp.data_nasterii || "").slice(0, 4);
        if (y !== fy) return false;
      }
      return true;
    });
  })();

  const toggleSelect = (p) => {
    setSelected((s) => {
      const n = { ...s };
      if (n[p.id]) delete n[p.id]; else n[p.id] = p;
      return n;
    });
  };

  const removeSelected = (pid) => {
    setSelected((s) => {
      const n = { ...s };
      delete n[pid];
      return n;
    });
  };

  const addFromSearch = (p) => {
    setSelected((s) => ({ ...s, [p.id]: p }));
    toast.success(`${p.date_personale?.nume} ${p.date_personale?.prenume} adăugat.`);
  };

  const savePreset = () => {
    try {
      localStorage.setItem(STORAGE_KEY, JSON.stringify(preset));
    } catch (e) { /* ignore */ }
  };

  const selectedList = Object.values(selected);
  const valEndISO = addMonthsISO(valStart, valMonths);
  const valStartRo = fmtRoFromISO(valStart);
  const valEndRo = fmtRoFromISO(valEndISO);

  const handlePrint = () => {
    if (selectedList.length === 0) {
      toast.info("Selectează cel puțin un jucător.");
      return;
    }
    savePreset();

    // Ensure valid conclusion for each; fallback to APT if missing
    const rows = selectedList
      .slice()
      .sort((a, b) => upperName(a).localeCompare(upperName(b), "ro"))
      .map((p, i) => {
        const conc = (p?.referat?.concluzie) || "APT";
        const label = CONCLUZIE_LABEL[conc] || CONCLUZIE_LABEL.APT;
        return `<tr>
          <td class="c">${i + 1}.</td>
          <td class="l">${upperName(p) || "—"}</td>
          <td class="c">${label}</td>
          <td class="c">${valStartRo} - ${valEndRo}</td>
        </tr>`;
      })
      .join("");

    const html = `<!DOCTYPE html><html lang="ro"><head><meta charset="utf-8"><title>Tabel jucători fotbal</title>
<style>
  @page { size: A4; margin: 16mm 14mm; }
  body { font-family: 'Times New Roman', Georgia, serif; color: #111; margin: 0; padding: 0; font-size: 12pt; line-height: 1.4; }
  .header-club { margin-bottom: 6px; }
  .club-name { font-weight: bold; font-size: 20pt; letter-spacing: 0.5px; margin: 0 0 8px 0; }
  .club-meta { font-size: 9.5pt; display: flex; flex-wrap: wrap; gap: 18px; border-bottom: 1px solid #666; padding-bottom: 6px; }
  .club-meta span { white-space: nowrap; }
  .nr-inreg { margin: 20px 0 8px 0; text-align: right; font-size: 11pt; }
  h2.title { text-align: center; font-size: 13pt; margin: 20px 0 18px; font-weight: bold; }
  h2.title sup { font-size: 8pt; font-weight: normal; }
  table.tabel { width: 100%; border-collapse: collapse; font-size: 11pt; margin-bottom: 30px; }
  table.tabel th, table.tabel td { border: 1px solid #000; padding: 5px 8px; vertical-align: middle; }
  table.tabel th { background: #f5f5f5; text-align: center; font-weight: bold; }
  table.tabel td.c { text-align: center; }
  table.tabel td.l { text-align: left; font-weight: 600; }
  .loc-data { margin: 24px 0 6px 0; font-size: 11pt; }
  .sig-grid { display: grid; grid-template-columns: 1fr 1fr; gap: 30px; margin-top: 8px; font-size: 11pt; }
  .sig-grid .col b { display: block; margin-bottom: 40px; text-transform: uppercase; }
  .footnote { margin-top: 40px; border-top: 1px solid #000; padding-top: 6px; font-size: 9.5pt; font-style: italic; max-width: 60%; }
  .footnote sup { font-size: 8pt; }
</style>
</head><body>
  <div class="header-club">
    <div class="club-name">${(preset.club_nume || "").toUpperCase()}</div>
    <div class="club-meta">
      ${preset.club_nume ? `<span>${preset.club_nume.toUpperCase()}</span>` : ""}
      ${preset.club_cif ? `<span>CIF: ${preset.club_cif}</span>` : ""}
      ${preset.club_adresa ? `<span>ADRESA: ${preset.club_adresa.toUpperCase()}</span>` : ""}
      ${preset.club_telefon ? `<span>TEL: ${preset.club_telefon}</span>` : ""}
    </div>
  </div>

  <div class="nr-inreg">Nr. Înregistrare: ${preset.nr_inregistrare || "_____________"}</div>

  <h2 class="title">Lista jucătorilor care au efectuat controlul medical și au obținut viza medicală standard<sup>1</sup></h2>

  <table class="tabel">
    <thead>
      <tr>
        <th style="width:8%;">Nr. Crt.</th>
        <th style="width:32%;">Numele și Prenumele</th>
        <th style="width:35%;">CONCLUZII</th>
        <th style="width:25%;">Valabilitate</th>
      </tr>
    </thead>
    <tbody>${rows}</tbody>
  </table>

  <div class="loc-data">Locul și data: ${preset.locul || "____________"}${preset.locul ? "," : ""} ${valStartRo}</div>

  <div class="sig-grid">
    <div class="col">
      <b>${(preset.director_sportiv || "").toUpperCase()},<br/>DIRECTOR SPORTIV</b>
      Semnătură<br/>Ștampilă
    </div>
    <div class="col">
      Medic,<br/>
      <b>${(preset.medic || "").toUpperCase()}</b>
      Semnătură<br/>Parafă
    </div>
  </div>

  <div class="footnote"><sup>1</sup> Valabilitatea vizei medicale standard este de ${valMonths} luni</div>
</body></html>`;

    const w = window.open("", "_blank", "width=900,height=1000");
    if (!w) { toast.error("Pop-up blocat — permite pop-up-urile."); return; }
    w.document.open();
    w.document.write(html);
    w.document.close();
    w.onload = () => setTimeout(() => { w.focus(); w.print(); }, 400);
    toast.success(`Tabel generat: ${selectedList.length} jucători.`);
  };

  return (
    <>
      <Dialog open={open} onOpenChange={onOpenChange}>
        <DialogContent className="max-w-4xl max-h-[92vh] overflow-y-auto" data-testid="tabel-fotbal-dialog">
          <DialogHeader>
            <DialogTitle className="flex items-center gap-2">
              <Trophy className="w-5 h-5 text-emerald-600" />
              Generează tabel fotbal — viza medicală standard
              {contextLabel && (
                <Badge variant="outline" className="ml-2 text-[10px] border-emerald-500 text-emerald-700" data-testid="tf-context-label">
                  {contextLabel}
                </Badge>
              )}
            </DialogTitle>
            <DialogDescription>
              {initialAthletes
                ? `Se pre-încarcă sportivii asociați acestui antrenor. Poți debifa/elimina sau adăuga manual alți jucători.`
                : `Se pre-încarcă fotbaliștii cu concluzie APT sau APT CONDIȚIONAT. Poți debifa/elimina sau adăuga manual alți jucători.`
              }
            </DialogDescription>
          </DialogHeader>

          {/* Header club */}
          <div className="rounded-lg border p-3 space-y-3">
            <div className="text-xs font-semibold text-muted-foreground uppercase tracking-wide">Antet document</div>
            <div className="grid grid-cols-1 md:grid-cols-2 gap-3">
              <div className="space-y-1 md:col-span-2">
                <Label>Nume club</Label>
                <Input value={preset.club_nume} onChange={(e) => setPreset((p) => ({ ...p, club_nume: e.target.value }))} data-testid="tf-club-nume" />
              </div>
              <div className="space-y-1">
                <Label>CIF</Label>
                <Input value={preset.club_cif} onChange={(e) => setPreset((p) => ({ ...p, club_cif: e.target.value }))} data-testid="tf-cif" />
              </div>
              <div className="space-y-1">
                <Label>Telefon</Label>
                <Input value={preset.club_telefon} onChange={(e) => setPreset((p) => ({ ...p, club_telefon: e.target.value }))} data-testid="tf-telefon" />
              </div>
              <div className="space-y-1 md:col-span-2">
                <Label>Adresa</Label>
                <Input value={preset.club_adresa} onChange={(e) => setPreset((p) => ({ ...p, club_adresa: e.target.value }))} data-testid="tf-adresa" />
              </div>
              <div className="space-y-1">
                <Label>Nr. Înregistrare</Label>
                <Input value={preset.nr_inregistrare} onChange={(e) => setPreset((p) => ({ ...p, nr_inregistrare: e.target.value }))} placeholder="ex: 123/2026" data-testid="tf-nr-inreg" />
              </div>
              <div className="space-y-1">
                <Label>Locul (opțional)</Label>
                <Input value={preset.locul} onChange={(e) => setPreset((p) => ({ ...p, locul: e.target.value }))} placeholder="ex: Câmpulung" data-testid="tf-locul" />
              </div>
              <div className="space-y-1">
                <Label>Director sportiv</Label>
                <Input value={preset.director_sportiv} onChange={(e) => setPreset((p) => ({ ...p, director_sportiv: e.target.value }))} placeholder="ex: Ghiță Costin Alin" data-testid="tf-director" />
              </div>
              <div className="space-y-1">
                <Label>Medic</Label>
                <Input value={preset.medic} onChange={(e) => setPreset((p) => ({ ...p, medic: e.target.value }))} data-testid="tf-medic" />
              </div>
            </div>
          </div>

          {/* Validity */}
          <div className="rounded-lg border p-3 space-y-3">
            <div className="text-xs font-semibold text-muted-foreground uppercase tracking-wide">Valabilitate viză</div>
            <div className="grid grid-cols-1 md:grid-cols-3 gap-3">
              <div className="space-y-1">
                <Label>Data emiterii</Label>
                <Input type="date" value={valStart} onChange={(e) => setValStart(e.target.value)} data-testid="tf-val-start" />
              </div>
              <div className="space-y-1">
                <Label>Durata (luni)</Label>
                <Input
                  type="number"
                  min="1"
                  max="24"
                  value={valMonths}
                  onChange={(e) => setValMonths(Math.max(1, Number(e.target.value) || 6))}
                  data-testid="tf-val-months"
                />
              </div>
              <div className="space-y-1">
                <Label>Valabil până la</Label>
                <Input value={valEndRo} readOnly className="bg-muted" data-testid="tf-val-end" />
              </div>
            </div>
          </div>

          {/* Selected players */}
          <div className="rounded-lg border p-3 space-y-2">
            <div className="flex items-center justify-between gap-2 flex-wrap">
              <div className="text-xs font-semibold text-muted-foreground uppercase tracking-wide">
                Jucători ({selectedList.length})
              </div>
              <Button size="sm" variant="outline" className="gap-1" onClick={() => setSearchOpen(true)} data-testid="tf-search-btn">
                <UserSearch className="w-3.5 h-3.5" /> Caută pacient
              </Button>
            </div>

            {loading ? (
              <div className="text-sm text-muted-foreground py-3 flex items-center gap-2">
                <Loader2 className="w-4 h-4 animate-spin" /> Se încarcă...
              </div>
            ) : eligible.length === 0 && selectedList.length === 0 ? (
              <div className="text-sm text-muted-foreground italic py-3 text-center">
                Niciun fotbalist cu APT/APT CONDIȚIONAT. Folosește <b>Caută pacient</b> pentru a adăuga manual.
              </div>
            ) : (
              <>
                {/* Eligible pool with checkboxes */}
                {eligible.length > 0 && (
                  <div className="space-y-1 max-h-56 overflow-y-auto border rounded-md p-2">
                    {eligible.map((p) => (
                      <label
                        key={p.id}
                        className="flex items-center gap-2 py-1 text-sm cursor-pointer hover:bg-muted/50 px-1 rounded"
                        data-testid={`tf-eligible-${p.id}`}
                      >
                        <Checkbox
                          checked={!!selected[p.id]}
                          onCheckedChange={() => toggleSelect(p)}
                        />
                        <span className="flex-1 truncate">
                          <span className="font-medium">{p.date_personale?.nume} {p.date_personale?.prenume}</span>
                          <span className="text-muted-foreground ml-2 text-xs">
                            #{p.numar_ordine} · {p.info_sportive?.club_sportiv || "—"}
                          </span>
                        </span>
                        <Badge variant="outline" className="text-[10px]">
                          {(() => {
                            const c = p.referat?.concluzie;
                            if (c === "APT_SUPRAVEGHERE") return "APT SUPRAV.";
                            if (c === "APT_INDIVIDUALIZAT") return "APT INDIV.";
                            if (c === "APT_CONDITIONAT") return "APT COND.";
                            if (c === "INAPT_TEMPORAR") return "INAPT TEMP.";
                            return "APT";
                          })()}
                        </Badge>
                      </label>
                    ))}
                  </div>
                )}

                {/* Additional selected (added via search — not in eligible) */}
                {selectedList.filter((p) => !eligible.find((e) => e.id === p.id)).length > 0 && (
                  <div className="space-y-1 border rounded-md p-2">
                    <div className="text-[10px] uppercase tracking-wide text-muted-foreground mb-1">Adăugați manual</div>
                    {selectedList.filter((p) => !eligible.find((e) => e.id === p.id)).map((p) => (
                      <div key={p.id} className="flex items-center gap-2 py-1 text-sm" data-testid={`tf-manual-${p.id}`}>
                        <span className="flex-1 truncate">
                          <span className="font-medium">{p.date_personale?.nume} {p.date_personale?.prenume}</span>
                          <span className="text-muted-foreground ml-2 text-xs">
                            #{p.numar_ordine} · {p.info_sportive?.sport_practicat || "—"}
                          </span>
                        </span>
                        <Badge variant="outline" className="text-[10px] border-blue-500 text-blue-700">manual</Badge>
                        <Button variant="ghost" size="icon" className="h-6 w-6 text-destructive" onClick={() => removeSelected(p.id)}>
                          <X className="w-3.5 h-3.5" />
                        </Button>
                      </div>
                    ))}
                  </div>
                )}
              </>
            )}
          </div>

          <DialogFooter>
            <Button variant="outline" onClick={() => onOpenChange(false)}>Anulează</Button>
            <Button
              onClick={handlePrint}
              disabled={selectedList.length === 0}
              className="gap-2 bg-emerald-600 hover:bg-emerald-700"
              data-testid="tf-print-btn"
            >
              <Printer className="w-4 h-4" /> Generează tabel ({selectedList.length})
            </Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>

      {/* Search dialog */}
      <Dialog open={searchOpen} onOpenChange={setSearchOpen}>
        <DialogContent className="max-w-2xl" data-testid="tf-search-dialog">
          <DialogHeader>
            <DialogTitle>Caută pacient</DialogTitle>
            <DialogDescription>
              Adaugă orice pacient în tabel (chiar dacă nu are sport=fotbal sau APT).
            </DialogDescription>
          </DialogHeader>
          <div className="relative">
            <Search className="w-4 h-4 absolute left-3 top-3 text-muted-foreground" />
            <Input
              className="pl-9"
              placeholder="Caută după nume, prenume, club (min 2 caractere)..."
              value={searchQ}
              onChange={(e) => setSearchQ(e.target.value)}
              autoFocus
              data-testid="tf-search-input"
            />
          </div>
          <div className="grid grid-cols-1 sm:grid-cols-3 gap-2 mt-2">
            <Input
              placeholder="Filtru: Club"
              value={filterClub}
              onChange={(e) => setFilterClub(e.target.value)}
              data-testid="tf-filter-club"
            />
            <Input
              placeholder="Filtru: Antrenor"
              value={filterAntrenor}
              onChange={(e) => setFilterAntrenor(e.target.value)}
              data-testid="tf-filter-antrenor"
            />
            <Input
              type="number"
              placeholder="Filtru: Anul nașterii"
              min="1900"
              max="2100"
              value={filterAn}
              onChange={(e) => setFilterAn(e.target.value)}
              data-testid="tf-filter-an-nasterii"
            />
          </div>
          {(filterClub || filterAntrenor || filterAn) && (
            <div className="flex items-center justify-between text-xs text-muted-foreground mt-1">
              <span data-testid="tf-filter-count">{filteredResults.length} din {searchResults.length} rezultate potrivite filtrelor</span>
              <button
                type="button"
                className="underline hover:text-slate-700"
                onClick={() => { setFilterClub(""); setFilterAntrenor(""); setFilterAn(""); }}
                data-testid="tf-clear-filters"
              >
                Șterge filtrele
              </button>
            </div>
          )}
          <div className="max-h-72 overflow-y-auto border rounded-md divide-y">
            {searchLoading && (
              <div className="p-3 text-sm text-muted-foreground flex items-center gap-2">
                <Loader2 className="w-4 h-4 animate-spin" /> Se caută...
              </div>
            )}
            {!searchLoading && searchQ.trim().length >= 2 && filteredResults.length === 0 && (
              <div className="p-3 text-sm text-muted-foreground italic">Niciun rezultat.</div>
            )}
            {!searchLoading && searchQ.trim().length < 2 && !filterClub && !filterAntrenor && !filterAn && (
              <div className="p-3 text-sm text-muted-foreground italic">Începe să scrii sau completează un filtru pentru a căuta.</div>
            )}
            {!searchLoading && filteredResults.map((p) => {
              const already = !!selected[p.id];
              return (
                <div key={p.id} className="p-2.5 flex items-center gap-3 text-sm" data-testid={`tf-search-result-${p.id}`}>
                  <span className="text-muted-foreground w-10 shrink-0 text-xs">#{p.numar_ordine}</span>
                  <div className="flex-1 min-w-0">
                    <div className="font-medium truncate">{p.date_personale?.nume} {p.date_personale?.prenume}</div>
                    <div className="text-xs text-muted-foreground truncate">
                      {p.info_sportive?.sport_practicat || "—"} · {p.info_sportive?.club_sportiv || "—"} · Concluzie: {p.referat?.concluzie || "—"}
                    </div>
                  </div>
                  {already ? (
                    <Badge variant="secondary" className="text-xs">Deja inclus</Badge>
                  ) : (
                    <Button size="sm" variant="outline" onClick={() => addFromSearch(p)} data-testid={`tf-add-${p.id}`}>
                      <Plus className="w-3 h-3 mr-1" /> Adaugă
                    </Button>
                  )}
                </div>
              );
            })}
          </div>
          <DialogFooter>
            <Button variant="outline" onClick={() => setSearchOpen(false)}>Închide</Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>
    </>
  );
}
